import * as React from 'react';
import {Navigation, Pagination, Scrollbar, A11y} from 'swiper';
import {ReactNode, useEffect, useState} from 'react';
import {Swiper, SwiperSlide} from 'swiper/react';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';

import ImpressionTracker from '../../analytics/impression-tracker/ImpressionTracker';
import {ErrorModal} from '../../error-handling/ErrorModal';
import {GaItemListName} from '../../../client/ga/ga-ecommerce.functions';
import {LocalStorageService} from '../../../client/local-storage/local-storage.service';
import {MonetateService} from '../../../client/monetate/monetate.service';
import {OrderableItem} from '../item.class';
import {OrdersService} from '../../../client/orders/orders.service';
import {Price} from '../../pricing/Price';
import {replaceHTMLQuotes} from '../../../client/components/helpers/stringHelper';
import {ShipRestrictionsModal} from '../ship-restrictions/ShipRestrictionsModal';
import {useOrderLines} from '../../lib/hooks/use-order-lines';
import {UserStateService} from '../../../client/users/user-state.service';
import {User} from '../../users/user.class';
import {useService} from '../../react/ServiceContext';
import {Spinner} from '../../ui/Spinner/Spinner';
import {PageHeader} from '../../ui/PageHeader/PageHeader';
import Button from '../../ui/Buttons/Button';
import {swiperSlideStyles} from '../../ui/Swiper/SwiperClasses';

interface ItemSpinnerProps {
    componentName: string;
    gaItemListName: GaItemListName;
    label?: string | ReactNode;
    loaded?: boolean;
    maxSlides?: number;
    orderableItems?: OrderableItem[];
    useAddToOrderModal?: boolean;
    user: User;
}

export const ItemSpinner = ({
    componentName,
    gaItemListName,
    label,
    loaded,
    maxSlides = 7,
    orderableItems,
    useAddToOrderModal,
    user,
}: ItemSpinnerProps) => {
    const localStorageService: LocalStorageService = useService(`localStorageService`);
    const monetateService: MonetateService = useService(`monetateService`);
    const ordersService: OrdersService = useService(`ordersService`);
    const userStateService: UserStateService = useService(`userStateService`);
    const [errorModal, setErrorModal] = useState({
        errorMsg: ``,
        show: false,
    });
    const [shipRestrictionsModal, setShipRestrictionsModal] = useState({
        show: false,
        orderableItem: undefined,
        unitsOrdered: 1,
    });
    const [slidePosition, setSlidePosition] = useState(0);

    // Process queued actions
    useEffect(() => {
        if (userStateService.hasPendingAddToOrder(componentName)) {
            addToOrder(userStateService.addToOrderItems, null, true, useAddToOrderModal);
        }
        // Only run once per page load
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Setup useOrderLines hooks
    const {addToOrder, orderLineErrors} = useOrderLines({
        componentName,
        gaItemListName,
    });

    // Listen to orderErrors
    useEffect(() => {
        if (orderLineErrors?.length > 0) {
            setErrorModal({
                errorMsg: orderLineErrors[0].message,
                show: true,
            });
        }
    }, [orderLineErrors]);

    /**
     * Sends recToken to Monetate
     * @param recToken
     */
    const clickEvents = (recToken: string) => {
        if (recToken) {
            monetateService.sendRecoEvents({
                recToken,
                eventType: `click`,
            });
        }
    };

    /**
     * Template
     */
    if (!loaded) {
        return <Spinner />;
    }
    if (!(orderableItems?.length > 0)) {
        return <></>;
    }
    return (
        <>
            {label && (
                <PageHeader
                    title={label}
                    type="h4"
                />
            )}
            <Swiper
                breakpoints={{
                    '0': {
                        slidesPerGroup: 1,
                        slidesPerView: 1,
                        spaceBetween: 20,
                    },
                    '420': {
                        slidesPerGroup: 2,
                        slidesPerView: 2,
                        spaceBetween: 20,
                    },
                    '640': {
                        slidesPerGroup: 3,
                        slidesPerView: 3,
                        spaceBetween: 20,
                    },
                    '768': {
                        slidesPerGroup: 3,
                        slidesPerView: 4,
                        spaceBetween: 40,
                    },
                    '1200': {
                        slidesPerGroup: 3,
                        slidesPerView: maxSlides,
                        spaceBetween: 50,
                    },
                }}
                className={swiperSlideStyles}
                modules={[Navigation, Pagination, Scrollbar, A11y]}
                navigation
                noSwipingSelector=".btn-red"
                onSlideChange={(slides) => {
                    setSlidePosition(slides?.realIndex);
                }}
                spaceBetween={50}
                watchSlidesProgress={true}
            >
                {orderableItems.map((orderableItem, index) => (
                    <SwiperSlide key={index}>
                        <div
                            className={`tw-flex tw-flex-col tw-w-[174px] tw-text-left tw-my-0 tw-mx-auto${
                                orderableItem.isDirectReplacement
                                    ? ' tw-border-[3px] tw-border-gray-600 tw-text-white tw-w-[174px] tw-p-3 last:!tw-border-r-[3px] last:!tw-border-r-gray-600'
                                    : ''
                            }`}
                        >
                            {orderableItem.isDirectReplacement && (
                                <div className="tw-m-[-1em] tw-p-2 tw-bg-gray-600 tw-white tw-text-center tw-mb-0">Direct Replacement</div>
                            )}
                            <div className="tw-my-[1em] tw-mx-0 tw-h-[146px] tw-flex tw-justify-center">
                                <ImpressionTracker
                                    data={{
                                        dimension16: ordersService?.currentOrderNumber,
                                        index,
                                        item_id: orderableItem.item,
                                        item_list_name: gaItemListName,
                                    }}
                                    detectPartial={false}
                                    slidePosition={slidePosition}
                                    trackImpressions={false}
                                >
                                    <a
                                        className="tw-text-blue-200 tw-no-underline tw-bg-transparent"
                                        href={`/item/${orderableItem.item}`}
                                        onClick={() => {
                                            localStorageService.removeItem(`searchTerm`);
                                            clickEvents(orderableItem.recToken);
                                        }}
                                    >
                                        <img
                                            alt={replaceHTMLQuotes(orderableItem.description)}
                                            className="tw-my-0 tw-mx-auto tw-max-h-[120px] tw-max-w-[120px]"
                                            src={`/ProductImageThumbs120${orderableItem.imagePath || '/noimage.png'}`}
                                        />
                                    </a>
                                </ImpressionTracker>
                            </div>
                            <div className="tw-mb-1 tw-text-gray-700">
                                <Price
                                    alias={componentName}
                                    isItemSpinner={true}
                                    item={orderableItem}
                                    user={user}
                                />
                                <div className="tw-text-sm tw-font-normal tw-leading-[1.3] tw-flex tw-flex-col">
                                    <div>Item: {orderableItem.prettyItem ? orderableItem.prettyItem : orderableItem.item}</div>
                                    <div>Pkg Qty: {orderableItem.pkgQty}</div>
                                </div>
                            </div>
                            <div
                                className="tw-text-sm tw-font-normal tw-leading-[1.3]"
                                style={{minHeight: 42}}
                            >
                                <ImpressionTracker
                                    data={{
                                        dimension16: ordersService?.currentOrderNumber,
                                        index,
                                        item_id: orderableItem.item,
                                        item_list_name: gaItemListName,
                                        recToken: orderableItem.recToken,
                                    }}
                                    slidePosition={slidePosition}
                                >
                                    <a
                                        className="tw-text-blue-200 tw-no-underline tw-bg-transparent"
                                        href={`/item/${orderableItem.item}`}
                                        onClick={() => {
                                            localStorageService.removeItem(`searchTerm`);
                                            clickEvents(orderableItem.recToken);
                                        }}
                                    >
                                        {orderableItem.description}
                                    </a>
                                </ImpressionTracker>
                            </div>
                            <div className="tw-flex tw-flex-row tw-flex-wrap tw-items-center tw-mt-4">
                                <input
                                    aria-label="Add to Order Quantity"
                                    className="tw-h-[calc(1.5em+0.75rem+2px)] tw-text-base tw-font-normal tw-leading-normal tw-bg-white tw-text-gray-700 tw-bg-clip-padding tw-border tw-block sm:tw-inline-block sm:tw-align-middle !tw-w-10 tw-p-0 tw-text-center tw-rounded-sm tw-mr-2"
                                    defaultValue={1}
                                    id={`cart-qty-${orderableItem.item}`}
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    type="number"
                                />
                                <Button
                                    onClick={() => {
                                        clickEvents(orderableItem.recToken);
                                        const unitsOrderedValue = parseInt(
                                            (document.getElementById(`cart-qty-${orderableItem.item}`) as HTMLInputElement).value,
                                        );
                                        if (!user.isLoggedIn() && orderableItem.excludeStArray) {
                                            setShipRestrictionsModal({
                                                show: true,
                                                orderableItem,
                                                unitsOrdered: unitsOrderedValue,
                                            });
                                        } else {
                                            localStorageService.removeItem(`searchTerm`);
                                            addToOrder(
                                                [
                                                    {
                                                        gaEcommerce: {item_list_name: gaItemListName},
                                                        item: orderableItem.item,
                                                        unitsOrdered: unitsOrderedValue,
                                                    },
                                                ],
                                                null,
                                                true,
                                                useAddToOrderModal,
                                            );
                                        }
                                    }}
                                    size="sm"
                                    type="button"
                                    variant="primary"
                                >
                                    Add to Order
                                </Button>
                            </div>
                        </div>
                    </SwiperSlide>
                ))}
            </Swiper>
            <ErrorModal
                errorMsg={errorModal.errorMsg}
                onClose={() => setErrorModal({show: false, errorMsg: ``})}
                show={errorModal.show}
            />
            <ShipRestrictionsModal
                addToOrder={addToOrder}
                gaItemListName={gaItemListName}
                onHide={() =>
                    setShipRestrictionsModal({
                        orderableItem: undefined,
                        show: false,
                        unitsOrdered: 1,
                    })
                }
                orderableItem={shipRestrictionsModal.orderableItem}
                show={shipRestrictionsModal.show}
                unitsOrdered={shipRestrictionsModal.unitsOrdered}
                useAddToOrderModal={useAddToOrderModal}
            />
        </>
    );
};
